import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentButton } from '@k3imagine/self-serve-components';

import GlobalContext from '../../../state/GlobalContext';
import { useLocalStorage } from '../../../state/useLocalStorage';
import { useLocalPrinter } from '../../../state/useLocalPrinter';
import {
  isLocalDevice,
  LOCAL_DEVICE,
  PaymentDevice,
  PaymentProviderEnum
} from '../../../services/payment/payment.type';
import { webSerial } from '../../../services/printer/local/web-serial.service';
import PaymentProvider from './PaymentProvider';
import LocalPrinterSettings from './LocalPrinterSettings';
import * as S from '../SupervisorModal.styles';

/**
 * Connector or local device.
 *
 * Connector: the existing payment provider list - payments and printing go
 * through the connector. Local device: receipts print on a printer attached
 * to this till, with no connector at all (cash and manual card only).
 */
const DeviceMode = () => {
  const { t } = useTranslation();
  const {
    selectedPaymentDevice,
    setSelectedPaymentDevice,
    availablePaymentDevices
  } = useContext(GlobalContext);
  const printer = useLocalPrinter();

  // The connector that was in use before switching to local, so switching
  // back does not mean picking it from the list again.
  const [lastConnector, setLastConnector] = useLocalStorage(
    'lastConnectorDevice'
  );

  const isLocal = isLocalDevice(selectedPaymentDevice);
  const isAndroid =
    selectedPaymentDevice?.type === PaymentProviderEnum.AndroidAppPayment;

  // The Android app prints through its own hardware; there is no choice here.
  if (isAndroid) {
    return (
      <>
        <S.SubTitle>{t('Supervisor.ChoosePaymentProvider')}:</S.SubTitle>
        <S.ComponentWrapper>
          <PaymentProvider />
        </S.ComponentWrapper>
      </>
    );
  }

  const selectLocal = () => {
    if (isLocal) return;
    if (selectedPaymentDevice) setLastConnector(selectedPaymentDevice);
    setSelectedPaymentDevice(LOCAL_DEVICE);
    // Reopen a printer granted in an earlier session. No prompt appears.
    if (!webSerial.isConnected()) webSerial.autoConnect();
  };

  const selectConnector = () => {
    if (!isLocal) return;
    const previous = (lastConnector as PaymentDevice | undefined)?.uniqueId;
    // Only restore it if the shop still has it configured.
    const restored = availablePaymentDevices.find(
      device => device.uniqueId === previous
    );
    setSelectedPaymentDevice(restored);
  };

  return (
    <>
      <S.SubTitle>{t('LocalPrinter.DeviceMode')}:</S.SubTitle>
      <S.ModeButtons>
        <S.ModeButtonWrapper>
          <PaymentButton
            label={t('LocalPrinter.Connector')}
            onClicked={selectConnector}
            theme={isLocal ? 'Light' : 'Dark'}
          />
        </S.ModeButtonWrapper>
        <S.ModeButtonWrapper>
          <PaymentButton
            label={t('LocalPrinter.LocalDevice')}
            onClicked={selectLocal}
            disabled={!printer.supported && !isLocal}
            theme={isLocal ? 'Dark' : 'Light'}
          />
        </S.ModeButtonWrapper>
      </S.ModeButtons>
      {!printer.supported && !isLocal && (
        <S.HintText>{t('LocalPrinter.Unsupported')}</S.HintText>
      )}

      {isLocal ? (
        <>
          <S.PanelText>{t('LocalPrinter.LocalDeviceInfo')}</S.PanelText>
          <LocalPrinterSettings />
        </>
      ) : (
        <>
          <S.SubTitle>{t('Supervisor.ChoosePaymentProvider')}:</S.SubTitle>
          <S.ComponentWrapper>
            <PaymentProvider />
          </S.ComponentWrapper>
        </>
      )}
    </>
  );
};

export default DeviceMode;
